import { RiErrorWarningLine, RiLinkUnlinkM } from "@remixicon/react";
import { Alert } from "@screener/uikit/components/alert";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldLabel } from "@screener/uikit/components/field";
import { IconTile } from "@screener/uikit/components/icon-tile";
import { Input } from "@screener/uikit/components/input";
import { type FormEvent, useCallback, useState } from "react";
import { type ResetPasswordResult, resetPassword } from "@/lib/auth-api";
import { describeFailure } from "@/login-form";
import { AuthHeading, AuthLayout } from "./auth-layout";

/** Что показывает страница, пока пароль не сменён. */
export type ResetPageState = "form" | "pending" | "link-invalid" | "busy" | "unavailable";

/**
 * Исход запроса в состояние страницы; `null` — пароль сменён, страница уступает
 * место форме входа. `switch` с `satisfies never`: новый исход не останется без экрана.
 */
export function resetPageState(result: ResetPasswordResult): ResetPageState | null {
  switch (result) {
    case "changed":
      return null;
    case "link-invalid":
    case "busy":
    case "unavailable":
      return result;
    default:
      return result satisfies never;
  }
}

/** Сообщение над формой при перегрузке и сбое. */
function describeResetFailure(state: "busy" | "unavailable"): string {
  return state === "busy" ? "Сервер занят. Попробуйте позже." : describeFailure("unavailable");
}

interface PasswordResetPageViewProps {
  state: ResetPageState;
  password: string;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  /** Переход к экрану запроса новой ссылки. */
  onRequestNew: () => void;
}

/** Разметка страницы нового пароля: форма либо «Ссылка недействительна». */
export function PasswordResetPageView({
  state,
  password,
  onPasswordChange,
  onSubmit,
  onRequestNew,
}: PasswordResetPageViewProps) {
  if (state === "link-invalid") {
    return (
      <AuthLayout>
        <div
          data-slot="invalid-reset-link"
          className="flex flex-col items-center gap-5 text-center"
        >
          <IconTile variant="destructive">
            <RiLinkUnlinkM className="size-[18px]" />
          </IconTile>
          <AuthHeading title="Ссылка недействительна, запросите новую" />
          <Button type="button" size="lg" className="w-full" onClick={onRequestNew}>
            Запросить новую ссылку
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const pending = state === "pending";
  const failure = state === "busy" || state === "unavailable" ? describeResetFailure(state) : null;
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6" aria-label="Новый пароль">
        <AuthHeading title="Новый пароль" />
        {failure !== null ? (
          <Alert variant="error" size="sm">
            <RiErrorWarningLine aria-hidden="true" />
            <span>{failure}</span>
          </Alert>
        ) : null}
        <Field>
          <FieldLabel className="text-xs">Новый пароль</FieldLabel>
          <Input
            size="lg"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            disabled={pending}
            required
          />
        </Field>
        <Button type="submit" size="lg" className="w-full" disabled={pending}>
          {pending ? "Сохранение…" : "Сменить пароль"}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Отправитель — параметр, чтобы проверять без сети. */
export function submitReset(
  token: string,
  password: string,
  send: (token: string, password: string) => Promise<ResetPasswordResult> = resetPassword,
): Promise<ResetPasswordResult> {
  return send(token, password);
}

interface PasswordResetPageProps {
  /** Токен из ссылки письма; в разметку и в хранилища не попадает. */
  token: string;
  /** Пароль сменён (`204`): приложение показывает форму входа. */
  onChanged: () => void;
  /** «Запросить новую ссылку» на экране недействительной ссылки. */
  onRequestNew: () => void;
  /** Отправитель — параметр, чтобы проверять без сети. */
  resetRequest?: (token: string, password: string) => Promise<ResetPasswordResult>;
}

/**
 * Страница нового пароля (`#/reset/{token}`, stage9-password-reset, Р6).
 * Пароль живёт только в состоянии формы и очищается после ответа; запрос
 * идёт по действию человека, а не на монтирование.
 */
export function PasswordResetPage({
  token,
  onChanged,
  onRequestNew,
  resetRequest = resetPassword,
}: PasswordResetPageProps) {
  const [password, setPassword] = useState("");
  const [state, setState] = useState<ResetPageState>("form");

  const handleSubmit = useCallback(() => {
    if (state === "pending" || password === "") {
      return;
    }
    setState("pending");
    submitReset(token, password, resetRequest).then((result) => {
      setPassword("");
      const next = resetPageState(result);
      if (next === null) {
        onChanged();
        return;
      }
      setState(next);
    });
  }, [state, password, token, resetRequest, onChanged]);

  return (
    <PasswordResetPageView
      state={state}
      password={password}
      onPasswordChange={setPassword}
      onSubmit={handleSubmit}
      onRequestNew={onRequestNew}
    />
  );
}
